import React, { createContext, ReactNode, useEffect, useState } from 'react';
import api from '../services/api';
import { JwtService } from '../services/jwtService';
import { ensureSession, refreshSession } from '../services/sessionService';
import { Role } from '../domain/Role';
import { getUserDisplayName } from '../utils/UserDisplayName';

interface User {
  name: string;
  email: string;
  access_token?: string;
  roles: Role[];
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const buildUser = (token: string): User => {
  const payload = JSON.parse(atob(token.split('.')[1]));
  return {
    name: getUserDisplayName(payload),
    email: payload.email,
    access_token: token,
    roles: payload.realm_access.roles || []
  };
};

const secondsUntilExpiration = (token: string): number => {
  const payload = JSON.parse(atob(token.split('.')[1]));
  return payload.exp - Date.now() / 1000;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const startSession = (token: string) => {
    setUser(buildUser(token));
    setIsAuthenticated(true);
  };

  useEffect(() => {
    ensureSession().then(token => {
      if (token) {
        startSession(token);
      }
    });
  }, []);

  useEffect(() => {
    const token = JwtService.getJwt();
    if (!token) {
      return;
    }

    const secondsLeft = secondsUntilExpiration(token);
    if (secondsLeft <= 0) {
      refreshToken();
      return;
    }

    const timer = setTimeout(refreshToken, secondsLeft * 1000);
    return () => clearTimeout(timer);
  }, [user]);

  const login = async (email: string, password: string) => {
    JwtService.clear();

    const response = await api.post('/login', {
      username: email,
      password: password,
    });

    JwtService.saveSession(response.data);
    startSession(response.data.access_token);
  };

  const logout = () => {
    JwtService.clear();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Función para renovar el access token
  const refreshToken = async () => {
    if (!JwtService.getRefreshToken()) {
      logout();
      return;
    }

    const accessToken = await refreshSession();
    if (accessToken) {
      startSession(accessToken);
    } else {
      logout();
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
